(() => {
  'use strict';
  const fallback = 'Contact us for availability';
  const cache = new Map();
  const pending = new Map();
  const ttl = 30000;
  let timer;
  let running = false;
  let rerun = false;

  async function read(url, json = false) {
    const key = String(url);
    const saved = cache.get(key);
    if (saved && Date.now() - saved.at < ttl) return saved.value;
    if (pending.has(key)) return pending.get(key);
    const request = (async () => {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(`Add-on availability unavailable (${response.status})`);
      const value = json ? await response.json() : await response.text();
      cache.set(key, { at: Date.now(), value });
      return value;
    })();
    pending.set(key, request);
    try { return await request; } finally { pending.delete(key); }
  }

  function variantFor(row, product) {
    if (product.variants.length === 1) return product.variants[0];
    const controls = [...row.querySelectorAll('select, input[name*="variant"]:checked')];
    for (const control of controls) {
      const id = String(control.value || '').match(/\d+$/)?.[0];
      const title = control.selectedOptions?.[0]?.textContent.trim();
      const exact = product.variants.find(v => String(v.id) === id || (title && v.title === title));
      if (exact) return exact;
    }
    // Never apply a different variant's promise when the selected variant is unknown.
    return null;
  }

  async function stateFor(row) {
    const link = row.querySelector('a.ast-addon-item-title[href]');
    if (!link) return null;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) return null;
    url.search = ''; url.hash = '';
    const product = await read(`${url.href}.js`, true);
    const variant = variantFor(row, product);
    if (!variant) return null;
    url.searchParams.set('variant', String(variant.id));
    url.searchParams.set('section_id', 'main-product');
    const html = await read(url.href);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const marker = doc.querySelector(`[data-demon-preorder-marker][data-variant-id="${String(variant.id)}"]`);
    if (!marker) return null;
    return { preorder: marker.dataset.isPreorder === 'true', message: marker.dataset.message?.trim() || fallback };
  }

  function remove(row) {
    if (!row) return;
    row.querySelectorAll('[data-demon-addon-decoration]').forEach(node => node.remove());
    delete row.dataset.demonAddonUnknownEta;
  }

  function decorate(row, state) {
    if (!state?.preorder) { remove(row); return; }
    const price = row.querySelector('.ast-addon-price-container');
    if (!price) return;
    let badge = row.querySelector('[data-demon-addon-decoration="badge"]');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'product-label product-label--preorder';
      badge.dataset.demonAddonDecoration = 'badge';
      badge.textContent = 'Pre-order';
      price.append(badge);
    }
    let eta = row.querySelector('.demon-addon-item-eta');
    const unknown = state.message === fallback;
    row.dataset.demonAddonUnknownEta = unknown ? 'true' : 'false';
    if (unknown) { eta?.remove(); return; }
    if (!eta) {
      eta = document.createElement('div');
      eta.className = 'demon-addon-item-eta';
      eta.dataset.demonAddonDecoration = 'eta';
      price.after(eta);
    }
    if (eta.textContent !== state.message) eta.textContent = state.message;
  }


  async function refresh() {
    if (running) { rerun = true; return; }
    running = true;
    try {
      const rows = [...document.querySelectorAll('.ast-addon .ast-addon-item')];
      for (let start = 0; start < rows.length; start += 3) {
        await Promise.all(rows.slice(start, start + 3).map(async row => {
          const selection = () => [row.querySelector('a.ast-addon-item-title')?.href, ...[...row.querySelectorAll('select')].map(select => select.value)].join('|');
          const before = selection();
          let state = null;
          try { state = await stateFor(row); } catch (_) { /* Do not invent availability on a failed read. */ }
          if (row.isConnected && before === selection()) decorate(row, state);
          else if (row.isConnected) rerun = true;
        }));
      }
    } finally {
      running = false;
      if (rerun) { rerun = false; schedule(); }
    }
  }

  function schedule() { clearTimeout(timer); timer = setTimeout(refresh, 150); }
  function isOwnNode(node) {
    return node.nodeType === 1 && (node.matches('[data-demon-addon-decoration]') || node.closest('[data-demon-addon-decoration]'));
  }
  function start() {
    const observer = new MutationObserver(records => {
      const changed = records.some(record => {
        if (isOwnNode(record.target)) return false;
        const nodes = [...record.addedNodes, ...record.removedNodes];
        if (nodes.length && nodes.every(isOwnNode)) return false;
        return record.target.nodeType === 1 && record.target.closest('.ast-addon') ||
          nodes.some(node => node.nodeType === 1 && (node.matches('.ast-addon') || node.querySelector('.ast-addon')));
      });
      if (changed) schedule();
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'aria-selected', 'data-variant-id'] });
    document.addEventListener('change', event => {
      if (event.target.closest('.ast-addon')) { remove(event.target.closest('.ast-addon-item')); schedule(); }
    });
    window.addEventListener('focus', schedule);
    window.addEventListener('pageshow', schedule);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
